import { useState, useEffect, useRef } from 'react';
import { useDiscovery } from '../context/DiscoveryContext';

// Same hero images used on the homepage, in a different order for variety
const CAROUSEL_IMAGES = [
  '/hero-image/f626svcxthrmw0cvzgvbe1cs8r_1769463035073.webp',
  '/hero-image/fcnn6v18mdrmy0cw41taz2bm2g_1770071053876.webp',
  '/hero-image/generated-88a70d05-8b00-40cf-bb9e-9a601b3ddbc7-b0ceb1ff-c76d-4bc8-8934-492ceee9c5ea-1.webp',
  '/hero-image/b0zkkxgvhhrmt0cvwj1tknk558_1769065570598.webp',
  '/hero-image/mmg7t737d9rmt0cw41wrvmd6a8_1770071386061.webp',
  '/hero-image/generated-b9217861-afc6-47a3-9b53-05afc1d95ac5-f6f42ce7-e379-4be9-996c-700aff454b1d-1.webp',
  '/hero-image/zenymbqs31rmy0cvzgsan2frr0_1769462798372.webp',
  '/hero-image/generated-2510dc10-b629-4c10-8d1c-9554633a8228-7f0036d7-3d5d-4835-b1de-08500211aec9-3.webp',
  '/hero-image/generated-95852dca-1d11-4fbd-af88-b728224ad448-6a45d7d8-3d2d-418f-8b05-c75060281854-0 (1).webp',
];

const CAROUSEL_INTERVAL_MS = 4000;

export const DiscoveryBackground = () => {
  const { state } = useDiscovery();
  const isIndustrySelected = !!state.industry?.backgroundImage;
  const industryBg = state.industry?.backgroundImage;

  // Carousel state for the default/industry-selection step
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselTimerRef = useRef<ReturnType<typeof setInterval>>();

  // Run carousel only when no industry is selected
  useEffect(() => {
    if (isIndustrySelected) {
      if (carouselTimerRef.current) clearInterval(carouselTimerRef.current);
      return;
    }

    carouselTimerRef.current = setInterval(() => {
      setCarouselIndex(prev => (prev + 1) % CAROUSEL_IMAGES.length);
    }, CAROUSEL_INTERVAL_MS);

    return () => {
      if (carouselTimerRef.current) clearInterval(carouselTimerRef.current);
    };
  }, [isIndustrySelected]);

  // Determine active image source
  const currentBg = isIndustrySelected
    ? industryBg!
    : CAROUSEL_IMAGES[carouselIndex];

  const [layers, setLayers] = useState<{ src: string; opacity: number }[]>([
    { src: currentBg, opacity: 1 },
  ]);
  const prevBgRef = useRef(currentBg);

  useEffect(() => {
    if (currentBg === prevBgRef.current) return;

    // Preload the new image before starting transition
    const img = new Image();
    img.src = currentBg;

    const startTransition = () => {
      setLayers([
        { src: prevBgRef.current, opacity: 0 },
        { src: currentBg, opacity: 1 },
      ]);

      // Clean up old layer after transition completes
      const timeout = setTimeout(() => {
        setLayers([{ src: currentBg, opacity: 1 }]);
      }, 1200);

      prevBgRef.current = currentBg;
      return () => clearTimeout(timeout);
    };

    if (img.complete) {
      startTransition();
    } else {
      img.onload = startTransition;
      img.onerror = startTransition;
    }
  }, [currentBg]);

  return (
    <div className="absolute inset-0">
      {layers.map((layer, i) => (
        <img
          key={`${layer.src}-${i}`}
          src={layer.src}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: layer.opacity * 0.4,
            transition: 'opacity 1200ms ease-in-out',
          }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/80 via-black/70 to-blue-900/80" />
    </div>
  );
};
